// "ERA auto-post" — Practice Details card, super_admin only.
//
// The switch that posts a clean 835 without a person (backend:
// services/era_auto_post_service.py; the checks are listed in
// docs/ledger-review.md, "Posting"). Off for every practice until someone
// turns it on here. PracticeDetailsPage renders this for super_admin only and
// the backend refuses everyone else, so the card itself does not gate. The
// count is the last 30 days of ERAs the switch posted, so the person flipping
// it can see it is doing something; the held count is what is waiting in the
// queue with a reason. The hold list is the adjustment codes this practice
// holds an ERA for (CO-18, OA-23, CO-45>charge by default).
//
// Nothing here names a patient, a payer or a check: the card reads booleans,
// counts, a dollar total and a list of codes.

import React, { useEffect, useRef, useState } from "react";
import "./EraAutoPost.css";

const API_URL = process.env.REACT_APP_API_URL || "";

const url = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/era-auto-post`;

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || fallback);
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

export function fetchEraAutoPost(practiceId) {
  return request(
    url(practiceId),
    {},
    "Could not load the ERA auto-post setting",
  );
}

function put(practiceId, body, fallback) {
  return request(
    url(practiceId),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    fallback,
  );
}

export function saveEraAutoPost(practiceId, enabled) {
  return put(
    practiceId,
    { enabled },
    "Could not save the ERA auto-post setting",
  );
}

// `codes` is the list, or null to return to the default.
export function saveHoldCarcs(practiceId, codes) {
  return put(practiceId, { hold_carcs: codes }, "Could not save the hold list");
}

export function formatMoney(amount) {
  const n = Number(amount || 0);
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Comma-separated; spaces inside a token ("CO-45 > charge") are the backend's
// to normalise, so the split is on commas only.
export function parseHoldCarcs(text) {
  return String(text || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

export default function EraAutoPostCard({ practiceId }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingHolds, setEditingHolds] = useState(false);
  const [holdText, setHoldText] = useState("");
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    setSummary(null);
    setError(null);
    setEditingHolds(false);
    fetchEraAutoPost(practiceId)
      .then((body) => {
        if (!cancelled) setSummary(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const toggle = async () => {
    if (busy || !summary) return;
    const next = !summary.enabled;
    if (
      next &&
      !window.confirm(
        "Turn on ERA auto-post? New ERAs that pass every check will be posted without anyone pressing Post, at arrival and by the hourly job. ERAs that fail a check are held in the queue with the reason. ERAs already waiting are not posted until someone runs the backfill.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await saveEraAutoPost(practiceId, next);
      if (currentPractice.current === practiceId) setSummary(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const startEditingHolds = () => {
    setHoldText((summary.hold_carcs || []).join(", "));
    setEditingHolds(true);
  };

  const saveHolds = async (codes) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = await saveHoldCarcs(practiceId, codes);
      if (currentPractice.current === practiceId) {
        setSummary(body);
        setEditingHolds(false);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const holdList = summary
    ? (summary.hold_carcs || []).join(", ") || "none"
    : "";

  return (
    <div
      className="integrations-section era-auto-post"
      data-testid="era-auto-post-card"
    >
      <div className="era-auto-post-header">
        <h3>ERA auto-post</h3>
        {summary && (
          <span
            className={`era-auto-post-badge ${summary.enabled ? "on" : "off"}`}
          >
            {summary.migration_pending
              ? "Pending"
              : summary.enabled
                ? "On"
                : "Off"}
          </span>
        )}
      </div>
      <p className="era-auto-post-explainer">
        When on, a new ERA that passes every check is posted without anyone
        pressing Post, at arrival and by the hourly job, recorded as posted by
        auto:era_auto_post with the check results on the ERA. An ERA is held for
        a person when any claim is unmatched, a claim is already posted at the
        same payer sequence, the check or line totals do not add up, it carries
        a PLB, a reversal or a negative payment, a payment exceeds a charge, a
        line carries a code on the hold list, it looks like a duplicate, or the
        payer is new to this practice. A denial on an otherwise clean ERA is
        posted as a denial, exactly as pressing Post would.
      </p>
      {error && (
        <div className="era-auto-post-error">
          {error}
          <button
            type="button"
            className="era-auto-post-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {summary && summary.migration_pending ? (
        <div className="era-auto-post-row">
          <span
            className="era-auto-post-muted"
            data-testid="era-auto-post-pending"
          >
            Migration pending: the switch is unavailable until{" "}
            {summary.migration} has run on this database. Nothing auto-posts
            until then.
          </span>
        </div>
      ) : summary ? (
        <>
          <div className="era-auto-post-row">
            <span className="era-auto-post-count">
              {summary.auto_posted_count} ERA
              {summary.auto_posted_count === 1 ? "" : "s"} auto-posted in the
              last {summary.days} days ({formatMoney(summary.auto_posted_total)}
              )
              {typeof summary.held_count === "number"
                ? ` · ${summary.held_count} held in the queue`
                : ""}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={toggle}
              disabled={busy}
            >
              {busy ? "Saving…" : summary.enabled ? "Turn off" : "Turn on"}
            </button>
          </div>
          <div
            className="era-auto-post-row era-auto-post-holds"
            data-testid="era-auto-post-holds"
          >
            {editingHolds ? (
              <>
                <label className="era-auto-post-muted" htmlFor="era-hold-carcs">
                  Hold on codes (comma-separated, e.g. CO-18, OA-23,
                  CO-45&gt;charge):
                </label>
                <input
                  id="era-hold-carcs"
                  type="text"
                  value={holdText}
                  onChange={(e) => setHoldText(e.target.value)}
                  disabled={busy}
                />
                <span className="era-auto-post-hold-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      const codes = parseHoldCarcs(holdText);
                      if (
                        codes.length === 0 &&
                        !window.confirm(
                          "Save an empty hold list? No adjustment code will hold an ERA for review for this practice.",
                        )
                      ) {
                        return;
                      }
                      saveHolds(codes);
                    }}
                    disabled={busy}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => saveHolds(null)}
                    disabled={busy}
                    title={`Return to the default list: ${(summary.hold_carcs_default || []).join(", ")}`}
                  >
                    Reset to default
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditingHolds(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </span>
              </>
            ) : (
              <>
                <span className="era-auto-post-muted">
                  Holds on codes: {holdList}
                  {summary.hold_carcs_migration_pending
                    ? ` (default; editable once ${summary.checks_migration} has run)`
                    : ""}
                </span>
                {!summary.hold_carcs_migration_pending && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={startEditingHolds}
                    disabled={busy}
                  >
                    Edit hold list
                  </button>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        !error && <div className="era-auto-post-muted">Loading…</div>
      )}
    </div>
  );
}
